import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { getDocContext } from "../../services/doc/docApi";
import { DocumentUtils } from "../../services/doc/internal/yDocHighLevelAPI";
import { createYElement } from "../../services/doc/internal/yDocLowLevelAPI";
import { API_BASE_URL } from "../../../../api/config";

type InsertLocation = { before?: string; after?: string } | { at: 'end' | 'start' } | { atIndex: number };

/**
 * doc_generate_image - AI-powered image generation and insertion
 * 
 * Generates an image using OpenAI's DALL-E 3 API and inserts it into the document.
 * The AI specifies what image to create and where to place it.
 */
export const docGenerateImageCommand: SimpleDocCommand = {
  id: 'doc_generate_image',
  description: `Generate an AI image and insert it into a Document.

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- For Notes/Sheets/Canvas/Slides, this tool is not available

Prompt must be 10-2000 characters. Specify insertLocation: {"at": "start"|"end"} or {"before"|"after": "node_id"}`,
  logicalName: 'doc_generate_image',
  version: 'v1',
  appType: 'docs',
  
  // ✅ Structured view data for UI rendering
  formatViewData: (result: any, toolInput: any) => {
    try {
      return {
        type: 'image_generation',
        summary: result?.message || 'Generated and inserted image',
        data: {
          prompt: result?.data?.prompt || toolInput?.prompt,
          imageData: result?.data?.imageData, // ← Base64 image for thumbnail
          model: result?.data?.model || 'DALL-E 3',
          provider: result?.data?.provider,
          size: result?.data?.size || '1792x1024',
          displayWidth: result?.data?.displayWidth || '67%',
          displayHeight: result?.data?.displayHeight || 'auto',
          alignment: result?.data?.alignment || 'center',
          imageIndex: result?.data?.imageIndex
        },
        meta: {
          duration: result?.data?.executionTime || result?.metadata?.executionTime
        }
      };
    } catch (error) {
      console.warn('[DOC_GENERATE_IMAGE] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Generated image',
        data: {}
      };
    }
  },
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document ID where the image will be inserted (e.g. "doc_large_pig_coconut" or "file_dark_star_dog")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          if (value.startsWith('note_') || value.startsWith('sheet_') || value.startsWith('canvas_') || value.startsWith('slide_') || value.startsWith('presentation_')) {
            return 'doc_generate_image is for Documents only. Cannot generate images in Notes/Sheets/Canvas/Slides.';
          }
          if (value.startsWith('pdf_') || value.startsWith('file_')) {
            return 'doc_generate_image is for Documents only. Cannot generate images in PDFs.';
          }
          return null;
        }
      },
      {
        name: 'prompt',
        type: 'string',
        description: 'Detailed description of the image to generate - be specific about subject, setting, style, and mood for best results',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Image prompt cannot be empty';
          if (value.length < 10) return 'Image prompt too short - be more descriptive (min 10 characters)';
          if (value.length > 2000) return 'Image prompt too long (max 2000 characters)';
          return null;
        }
      },
      {
        name: 'insertLocation',
        type: 'object',
        description: `Where to insert the image:
  {"at": "start"} - Insert at document beginning
  {"at": "end"} - Insert at document end
  {"before": "node_abc_123"} - Insert before this node
  {"after": "node_abc_123"} - Insert after this node`,
        required: true,
        validator: (value: any) => {
          if (!value || typeof value !== 'object') return 'Insert location must be an object';
          if (!value.at && !value.before && !value.after) {
            return 'Insert location must have "at", "before", or "after" property';
          }
          return null;
        }
      },
      {
        name: 'uncensored',
        type: 'boolean',
        description: 'Image generation mode. false (default): regular content-filtered model. true: uncensored model for content that may trigger filters.',
        required: false,
        defaultValue: false
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      // Validate required parameters
      const { id: docId, prompt, insertLocation, uncensored } = params;
      
      if (!docId || !prompt || !insertLocation) {
        return createErrorResult('Missing required parameters', 'id, prompt, and insertLocation are required', 'validation');
      }
      
      console.log('[doc_generate_image] Starting image generation:', {
        docId,
        prompt: prompt.substring(0, 100) + (prompt.length > 100 ? '...' : ''),
        insertLocation,
        uncensored: uncensored || false
      });
      
      // Get document context
      const { doc, error } = await getDocContext(docId, context);
      if (error || !doc) {
        // Provide helpful context about available documents
        let availableDocsHelp = '';
        try {
          const windowContents = (context as any)?.spaceContext?.entityRefs || {};
          const availableDocs = Object.keys(windowContents)
            .filter(key => key.startsWith('doc_'))
            .map(key => {
              const doc = windowContents[key];
              return `  - ${key} (${doc?.title || doc?.entityName || 'Untitled'})`;
            });
          
          if (availableDocs.length > 0) {
            availableDocsHelp = `\n\nAvailable documents in context:\n${availableDocs.join('\n')}\n\nPlease use one of these document IDs, or ask the user to open the document first.`;
          } else {
            availableDocsHelp = '\n\nNo documents are currently open or in context. Please ask the user to open a document first, or use doc_read_v2 to read an existing document into context.';
          }
        } catch (e) {
          availableDocsHelp = '\n\nCould not retrieve available documents. Please ensure a document is open before generating images.';
        }
        
        return createErrorResult(
          'Document not accessible', 
          `Cannot insert image: Document "${docId}" is not accessible. ${error || 'Document not found.'}${availableDocsHelp}`, 
          'validation'
        );
      }
      
      const { yDoc: ydoc, yxml } = doc;
      
      // STEP 1: Generate image using hybrid API (OpenAI default, Grok for uncensored)
      const useUncensored = uncensored === true;
      const providerName = useUncensored ? 'GROK (uncensored)' : 'OPENAI DALL-E 3 (default)';
      
      console.log(`[doc_generate_image] Calling hybrid image generation API with provider: ${providerName}`);
      console.log(`🎨 [doc_generate_image] Using landscape format: 1792x1024`);
      
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/image/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          prompt,
          size: '1792x1024',
          quality: 'medium',
          uncensored: useUncensored,
          variants: 1
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[doc_generate_image] API error:', response.status, errorText);
        return createErrorResult('Image generation failed', `API error (${response.status}): ${errorText}`, 'execution');
      }
      
      const result = await response.json();
      
      if (!result.success || !result.images || result.images.length === 0) {
        console.error('[doc_generate_image] No images returned:', result);
        return createErrorResult('Image generation failed', 'No images returned from API', 'execution');
      }
      
      const base64Image = result.images[0]; // Use first generated image
      console.log('[doc_generate_image] Image generated successfully:', {
        imageSize: base64Image.length,
        provider: result.provider,     // Which provider was used
        model: result.model,
        size: result.size,
        quality: result.quality,
        style: result.style,
        uncensored: result.uncensored  // Mode used
      });
      
      // STEP 2: Calculate insertion index
      let insertionIndex: number;
      
      if ('atIndex' in insertLocation) {
        insertionIndex = insertLocation.atIndex;
      } else if ('at' in insertLocation) {
        insertionIndex = insertLocation.at === 'start' ? 0 : yxml.length;
      } else if ('before' in insertLocation || 'after' in insertLocation) {
        const targetNodeId = insertLocation.before || insertLocation.after;
        const targetNode = DocumentUtils.findNodeById(yxml, targetNodeId!);
        
        if (!targetNode) {
          return createErrorResult('Target node not found', `Node ID "${targetNodeId}" does not exist in the document.`, 'validation');
        }
        
        insertionIndex = targetNode.index + (insertLocation.after ? 1 : 0);
      } else {
        return createErrorResult('Invalid insertLocation format', 'Use {before: "node_id"}, {after: "node_id"}, {at: "start"}, {at: "end"}, or {atIndex: number}', 'validation');
      }
      
      // Ensure index is valid
      insertionIndex = Math.max(0, Math.min(insertionIndex, yxml.length));
      
      console.log('[doc_generate_image] Insertion index calculated:', {
        insertionIndex,
        documentLength: yxml.length
      });
      
      // STEP 3: Create Y.js image element
      // ✅ CRITICAL: Y.js attributes must be strings (see docsCRDTService.ts:1262)
      // Display size: 67% width (2/3 of document) with auto height, centered alignment
      console.log(`📐 [doc_generate_image] DISPLAY: width='67%', height='auto', alignment='center'`);
      
      const imageElement = createYElement('image', {
        attributes: {
          src: base64Image, // Base64 data URL - will auto-upload via ImageNodeView
          alt: '',  // No alt text by default
          title: prompt.substring(0, 100),
          width: '67%', // Display at 67% width (approximately 2/3 of document)
          height: 'auto', // Auto height to maintain aspect ratio
          alignment: 'center',  // Always centered
          borderRadius: '0', // String for Y.js
          shadow: 'none'
        },
        docId: docId,
        logPrefix: '[doc_generate_image]'
      });
      
      // STEP 4: Insert into document
      console.log('[doc_generate_image] Inserting image into document at index', insertionIndex);
      
      ydoc.transact(() => {
        yxml.insert(insertionIndex, [imageElement]);
      }, { origin: 'ai_image_generation' });
      
      console.log('[doc_generate_image] Image inserted successfully');
      
      // ✅ NEW: Export to markdown for showcase (with delay to ensure Y.Doc has settled)
      try {
        const { exportDocumentToMarkdown, shouldExportToMarkdown } = await import('../../utils/markdownExport');
        
        if (shouldExportToMarkdown(context)) {
          console.log('[doc_generate_image] 📤 Scheduling markdown export (with 200ms delay)');
          
          // ✅ FIX: Delay export to ensure Y.Doc has fully settled after image insertion
          setTimeout(() => {
            console.log('[doc_generate_image] 📤 Executing delayed markdown export');
            exportDocumentToMarkdown(docId, ydoc, {
              source: 'doc_generate_image',
              blocking: false,
              verbose: true
            }).catch(err => {
              console.error('[doc_generate_image] ❌ Delayed markdown export failed:', err);
            });
          }, 200); // 200ms delay to let Y.Doc settle
        }
      } catch (exportError) {
        console.error('[doc_generate_image] Markdown export scheduling failed (non-critical):', exportError);
        // Don't throw - image was successfully inserted, markdown export is secondary
      }
      
      const executionTime = Date.now() - startTime;
      
      return createSuccessResult(
        `Generated and inserted image: "${prompt.substring(0, 50)}${prompt.length > 50 ? '...' : ''}"`,
        {
          operation: 'generate_image',
          imageIndex: insertionIndex,
          prompt: prompt,
          imageData: base64Image, // ← Store full base64 image for thumbnail display
          size: '1792x1024',                  // Landscape format
          displayWidth: '67%',                // How it displays: 2/3 of document width
          displayHeight: 'auto',
          alignment: 'center',
          provider: result.provider,
          model: result.model,
          executionTime
        },
        {
          format: 'json',
          metadata: {
            executionTime,
            method: 'hybrid_image_generation'
          }
        }
      );
      
    } catch (error) {
      console.error('[doc_generate_image] Error:', error);
      return createErrorResult(
        'Failed to generate and insert image',
        error instanceof Error ? error.message : String(error),
        'execution'
      );
    }
  }
};

